import { ApiError } from '@omnipresentgroup/api';
import {
  Box,
  DateInput,
  formatISODate,
  List,
  Modal,
  type ModalProps,
  Notification,
  notifySuccess,
  Stack,
  Typography,
} from '@omnipresentgroup/design-system';
import {
  getTimeOffsKey,
  useClientSetupStatus,
  useTimeOffsUpload,
} from '@omnipresentgroup/domains';
import { Form } from '@omnipresentgroup/flexible-forms';
import type { DocumentUploadRequest } from '@omnipresentgroup/types';
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import { useManagerUser } from '../../../hooks';

const MODAL_TITLE = 'Upload time off';

const NOTIFICATION_TITLE =
  'Please upload a file containing the time off for a specific month and year';
const NOTIFICATION_DESCRIPTION =
  'Ensure that the start date and the end date of the time off specified in the file adhere to the following standard:';

const TIME_OFFS_SUCCESS_MESSAGE = 'Time off uploaded successfully';

const FORM_ID = 'upload-time-offs-form';

const INITIAL_DATE = new Date();

type Props = {
  modalProps: Omit<ModalProps, 'children'>;
  handleClose: () => void;
};

export const UploadTimeOffsModal = ({ modalProps, handleClose }: Props) => {
  const { companyId } = useManagerUser();

  const queryClient = useQueryClient();

  const [selectedDate, setSelectedDate] = useState<Date | null>(INITIAL_DATE);
  const selectedDateReference = useRef<Date | null>(INITIAL_DATE);

  const onChange = (date: Date | null) => {
    setSelectedDate(date);
    selectedDateReference.current = date;
  };

  const [uploadErrorProblems, setUploadErrorProblems] = useState<
    {
      id: string;
      name: string;
    }[]
  >([]);

  const { data: clientSetupStatus } = useClientSetupStatus();

  const { uploadTimeOffsFile } = useTimeOffsUpload();

  const uploadTimeOffs = (fileToUpload: DocumentUploadRequest) =>
    uploadTimeOffsFile({
      file: fileToUpload.fileToUpload,
      date: formatISODate(
        selectedDateReference.current?.toISOString() ?? '',
        'yyyy-MM',
      ),
    })
      .then(() => {
        setUploadErrorProblems([]);
        queryClient.invalidateQueries({ queryKey: getTimeOffsKey(companyId) });
        notifySuccess({
          title: TIME_OFFS_SUCCESS_MESSAGE,
        });
        setTimeout(() => {
          handleClose();
        }, 1000);
        return {
          fileName: fileToUpload.fileToUpload.name,
        };
      })
      .catch((error: ApiError) => {
        if (error instanceof ApiError) {
          setUploadErrorProblems(
            error.problems?.map((problem, index) => ({
              id: String(index),
              name: `${problem.title}. ${problem.detail} ${
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (problem.metadata?.details as any)?.loc.join(' > ') ?? ''
              }`,
            })) ?? [{ id: 'error', name: error.detail || error.title }],
          );
        } else {
          setUploadErrorProblems([
            {
              id: (error as Error).name,
              name: (error as Error).message,
            },
          ]);
        }
      });

  return (
    <Modal {...modalProps} span={6}>
      <Modal.Header title={MODAL_TITLE} onClose={handleClose} />

      <Modal.Body>
        <Stack gap="16">
          {clientSetupStatus && (
            <Notification
              id="time-offs-date-time-format"
              intent="info"
              title={NOTIFICATION_TITLE}
              description={`${NOTIFICATION_DESCRIPTION} ${clientSetupStatus.dateTimeFormat}`}
            />
          )}
          {uploadErrorProblems.length > 0 && (
            <Notification
              id="time-offs-upload-error"
              intent="error"
              title="Upload error"
              description={
                <List
                  showDisc
                  gap="0"
                  items={uploadErrorProblems}
                  keyExtractor="id"
                  render={(item) => (
                    <Typography as="span" size="14">
                      {item.name}
                    </Typography>
                  )}
                />
              }
            />
          )}

          <Box data-testing="blackout">
            <DateInput
              id="time-offs-month-year"
              label="Month and year"
              showMonthYearPicker
              selected={selectedDate}
              onChange={onChange}
            />
          </Box>
          <Form
            id={FORM_ID}
            schema={{
              type: 'object',
              properties: {
                timeOffsFile: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      id: {
                        type: 'string',
                      },
                      name: {
                        type: 'string',
                      },
                      url: {
                        type: 'string',
                      },
                      signature: {
                        type: 'string',
                      },
                    },
                  },
                },
              },
            }}
            uiSchema={{
              'ui:submitButtonOptions': {
                norender: true,
              },
              timeOffsFile: {
                'ui:field': 'file-upload',
                'ui:options': {
                  title: '',
                  accept: {
                    'image/*': ['.csv', '.xlsx'],
                  },
                  fileInfo: 'You can upload .csv or .xlsx files',
                },
              },
            }}
            formContext={{
              document: {
                uploadDocument: uploadTimeOffs,
              },
            }}
          />
        </Stack>
      </Modal.Body>
    </Modal>
  );
};
